import { z } from 'zod';

/**
 * Facturatie zoals het over de lijn komt.
 *
 * Elk schema volgt één API Resource uit `smartkeur-api` (tussen haakjes), of het
 * antwoord uit `02-api-contract.md` §Facturatie en tegoed waar geen Resource-klasse
 * bestaat. De schermen zien deze vorm nooit: `model.ts` maakt er het model van.
 *
 * Bedragen zijn strings met twee decimalen (`"1450.00"`), zodat JavaScript er
 * niets aan afrondt. Een verwijzing is altijd een `*_uuid`.
 */

/** ISO-8601 met tijdzone. */
const timestamp = z.string();

/** `"1450.00"` */
const amount = z.string();

/** (`Billing\InvoiceLineResource`) */
export const invoiceLineResourceSchema = z.object({
    description: z.string(),
    /** De dienst van de keuring die deze regel factureert; `null` bij een losse regel. */
    inspection_type: z
        .object({
            uuid: z.string(),
            code: z.string(),
            name: z.string(),
        })
        .nullable()
        .optional(),
    quantity: z.string(),
    unit_price: amount,
    vat_rate: z.string(),
    total: amount,
    vat_amount: amount,
    total_incl_vat: amount,
    sort_order: z.number(),
});

/** (`InvoiceTotals::breakdown()`) de btw per tarief onder een factuur. */
export const vatBreakdownResourceSchema = z.object({
    vat_rate: z.string(),
    base: amount,
    vat: amount,
});

/** (`Billing\InvoiceResource`) */
export const invoiceResourceSchema = z.object({
    uuid: z.string(),
    /** `null` zolang de factuur een concept is: het nummer komt pas bij versturen. */
    number: z.string().nullable(),
    /** Het nummer, of `concept 4F2K9Q` voor een concept. */
    label: z.string(),
    status: z.string(),
    status_label: z.string(),
    client_uuid: z.string().nullable(),
    client_name: z.string().nullable(),
    order_uuid: z.string().nullable(),
    order: z
        .object({
            uuid: z.string(),
            number: z.string(),
            address: z.string(),
        })
        .nullable(),
    reference: z.string().nullable(),
    notes: z.string().nullable(),
    subtotal: amount,
    vat_amount: amount,
    total: amount,
    payment_term_days: z.number(),
    issued_at: timestamp.nullable(),
    due_at: timestamp.nullable(),
    paid_at: timestamp.nullable(),
    has_pdf: z.boolean(),
    lines: z.array(invoiceLineResourceSchema).optional(),
    vat_breakdown: z.array(vatBreakdownResourceSchema).optional(),
    created_at: timestamp.nullable(),
});

export type InvoiceResource = z.infer<typeof invoiceResourceSchema>;

/** (`InvoiceBatchController`) de uitkomst van één factuur uit `POST /invoices/batch`. */
export const invoiceBatchItemResourceSchema = z.object({
    uuid: z.string(),
    number: z.string().nullable(),
    /** `null` bij `not_found`: dan is er geen factuur om een status van te geven. */
    status: z.string().nullable(),
    /** `sent` · `failed` · `forbidden` · `not_found` */
    result: z.string(),
    problem: z.string().nullable(),
});

export type InvoiceBatchItemResource = z.infer<typeof invoiceBatchItemResourceSchema>;

/** (`InvoiceBatchController`) */
export const invoiceBatchResourceSchema = z.object({
    results: z.array(invoiceBatchItemResourceSchema),
    sent: z.number(),
    failed: z.number(),
});

export type InvoiceBatchResource = z.infer<typeof invoiceBatchResourceSchema>;

/** (`InvoicePdfController`) een kortlevende, ondertekende link naar de PDF. */
export const invoicePdfLinkResourceSchema = z.object({
    url: z.string(),
    url_expires_in: z.number(),
    filename: z.string(),
    mime_type: z.string(),
    size_bytes: z.number(),
});

export type InvoicePdfLinkResource = z.infer<typeof invoicePdfLinkResourceSchema>;

/** (`Billing\TariffResource`) */
export const tariffResourceSchema = z.object({
    uuid: z.string(),
    inspection_type_uuid: z.string().nullable(),
    inspection_type: z
        .object({
            uuid: z.string(),
            code: z.string(),
            name: z.string(),
        })
        .nullable(),
    name: z.string(),
    price: amount,
    inspector_fee: amount,
    margin: amount,
    vat_rate: z.string(),
    /** `2026-09-16` */
    valid_from: z.string(),
    valid_until: z.string().nullable(),
    is_current: z.boolean(),
});

export type TariffResource = z.infer<typeof tariffResourceSchema>;

/** (`Catalog\InspectionTypeResource`) een dienst waar een tarief voor kan gelden. */
export const inspectionTypeResourceSchema = z.object({
    uuid: z.string(),
    code: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    color: z.string().nullable(),
    is_system: z.boolean(),
    is_platform: z.boolean(),
    sort_order: z.number(),
});

export type InspectionTypeResource = z.infer<typeof inspectionTypeResourceSchema>;

/** (`BillingSummaryController`, `MonthlyBillingSummary`) de maandkaart. */
export const billingSummaryResourceSchema = z.object({
    /** `2026-09` */
    period: z.string(),
    period_label: z.string(),
    from: z.string(),
    to: z.string(),
    invoice_count: z.number(),
    invoiced: amount,
    vat: amount,
    invoiced_incl_vat: amount,
    inspector_fees: amount,
    margin: amount,
    outstanding: amount,
    overdue: amount,
    overdue_count: z.number(),
});

export type BillingSummaryResource = z.infer<typeof billingSummaryResourceSchema>;

/** (`BillingIntegrationController`) de koppeling met het boekhoudpakket. */
export const billingIntegrationResourceSchema = z.object({
    connected: z.boolean(),
    status: z.string(),
    status_label: z.string(),
    provider: z.string().nullable(),
    provider_label: z.string().nullable(),
    connected_at: timestamp.nullable(),
    last_synced_at: timestamp.nullable(),
    message: z.string(),
});

export type BillingIntegrationResource = z.infer<typeof billingIntegrationResourceSchema>;

/**
 * (`Billing\SendableInvoiceResource`) `GET /orders/{order}/invoice`: kan de factuur
 * mee met het rapport van deze keuring, en welke factuur is het.
 */
export const orderInvoiceResourceSchema = z.object({
    /** `none` · `nothing_to_invoice` · `draft` · `rendering` · `ready` · `delivered` · `closed` */
    state: z.string(),
    state_label: z.string(),
    enclosable: z.boolean(),
    ready: z.boolean(),
    reason: z.string().nullable(),
    invoice_uuid: z.string().nullable(),
    number: z.string().nullable(),
    label: z.string().nullable(),
    total: amount.nullable(),
    due_at: timestamp.nullable(),
    mailed_at: timestamp.nullable(),
    filename: z.string().nullable(),
});

export type OrderInvoiceResource = z.infer<typeof orderInvoiceResourceSchema>;
